import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fmtDate, panelGet } from './kit';
import { KNOWN_SUPER_ADMINS } from './adminAccess';

/**
 * Small pieces shared by Access control (admin tabs) and the Feature access
 * tabs of For You & What's New and Ocean: the admin picker, the mode switch,
 * the "changed by" line and the admin list behind the picker.
 */

export interface AdminOption {
  email: string;
  name: string;
  active: boolean;
  /** Set when the server marks super admins in its admin list. */
  superAdmin?: boolean;
  /** 'team' = read-only team account (Admins & access → Admin accounts). */
  role?: 'admin' | 'team';
}

/** access_state.admins -> picker options, lower-cased and de-duplicated.
 *  `supers` marks the super admins (they always see everything, so they are
 *  never picked). */
export function readAdmins(raw: any, supers: string[] = []): AdminOption[] {
  const sup = new Set(supers.map(e => e.toLowerCase()));
  const seen = new Set<string>();
  const out: AdminOption[] = [];
  (Array.isArray(raw) ? raw : []).forEach((a: any) => {
    const email = String(typeof a === 'string' ? a : a?.email || '').trim().toLowerCase();
    if (!email || seen.has(email)) return;
    seen.add(email);
    out.push({
      email,
      name: typeof a === 'object' && a && typeof a.name === 'string' ? a.name : '',
      active: typeof a === 'object' && a ? a.active !== false : true,
      superAdmin: sup.has(email)
        || (typeof a === 'object' && a ? (a.super_admin === true || a.is_super_admin === true) : false),
      role: typeof a === 'object' && a && a.role === 'team' ? 'team' : 'admin',
    });
  });
  return out.sort((x, y) => Number(y.active) - Number(x.active) || x.email.localeCompare(y.email));
}

/** The admin list for a picker outside Access control (the Feature access
 *  tabs): access/access_state's `admins`, super admins marked. Both are super
 *  admins only. While it loads, or when the read failed, `loading` / `error`
 *  say so (the caller shows the error with a retry, and the picker does not
 *  call anyone "no longer an admin"); the rest of the tab keeps working.
 *  `skip` when the caller already has the list. */
export function useAdminRoster(skip = false): {
  admins: AdminOption[]; loading: boolean; error: string; reload: () => void;
} {
  const [st, setSt] = useState<{ admins: AdminOption[]; loading: boolean; error: string }>(
    { admins: [], loading: !skip, error: '' });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (skip) return undefined;
    let dead = false;
    setSt(s => ({ ...s, loading: true, error: '' }));
    (async () => {
      const { data, error } = await panelGet('access', 'access_state');
      if (dead) return;
      if (error || !data) { setSt(s => ({ admins: s.admins, loading: false, error: error || 'No answer' })); return; }
      const supers = Array.isArray(data?.super_admins) ? data.super_admins.map(String) : KNOWN_SUPER_ADMINS;
      setSt({ admins: readAdmins(data?.admins, supers), loading: false, error: '' });
    })();
    return () => { dead = true; };
  }, [skip, n]);
  return { ...st, reload: () => setN(x => x + 1) };
}

/** Whether a picker's admin list can be trusted yet: 'loading' / 'error' keep it from calling a
 *  picked admin "no longer an admin" just because the list is not there. */
export type AdminListState = 'ready' | 'loading' | 'error';

export const emailsOf = (raw: any): string[] =>
  Array.from(new Set((Array.isArray(raw) ? raw : [])
    .map((e: any) => String(typeof e === 'string' ? e : e?.email || '').trim().toLowerCase())
    .filter(Boolean))).sort();

export const sameList = (a: string[], b: string[]) => {
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.length === sb.length && sa.every((x, i) => x === sb[i]);
};

/** A row of mutually exclusive choices, one highlighted. */
export function ModeSwitch<T extends string>({ value, options, onChange, disabled, label }: {
  value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; disabled?: boolean; label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label}
      className="inline-flex flex-wrap rounded-full border border-primary-300 bg-white p-0.5">
      {options.map(o => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} disabled={disabled}
          onClick={() => { if (value !== o.value) onChange(o.value); }}
          className={`py-1 px-3 rounded-full text-xs font-grotesk whitespace-nowrap disabled:opacity-60 ${value === o.value
            ? 'bg-primary-500 text-grayscale-100 font-bold'
            : 'text-grayscale-1200 hover:bg-primary-100'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** "Changed by x · 26 Oct 2026, 14:02", or "Default" when never changed. */
export const ChangedLine: React.FC<{ by?: string; at?: string; isDefault?: boolean }> = ({ by, at, isDefault }) => (
  <span className="text-[11px] text-grayscale-900">
    {by || at
      ? `Changed${by ? ` by ${by}` : ''}${at ? ` · ${fmtDate(at)}` : ''}`
      : isDefault === false ? 'Not the default' : 'Default (never changed)'}
  </span>
);

/** Pick admins from the admin list: chips for the picked ones, a search box
 *  with matching admins underneath. Addresses not on the list can't be added
 *  — access is only ever given to existing admins. */
export const AdminPicker: React.FC<{
  admins: AdminOption[]; value: string[]; onChange: (v: string[]) => void; disabled?: boolean;
  /** Omitted: the caller's list is loaded (Access control shows its own errors). */
  listState?: AdminListState;
}> = ({ admins, value, onChange, disabled, listState }) => {
  const listed = !listState || listState === 'ready';
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLInputElement>(null);
  const byEmail = useMemo(() => {
    const m: Record<string, AdminOption> = {};
    admins.forEach(a => { m[a.email] = a; });
    return m;
  }, [admins]);
  const picked = new Set(value);
  const matches = admins
    .filter(a => !picked.has(a.email) && !a.superAdmin)
    .filter(a => {
      const s = q.trim().toLowerCase();
      return !s || a.email.includes(s) || a.name.toLowerCase().includes(s);
    })
    .slice(0, 8);
  const add = (email: string) => { onChange([...value, email].sort()); setQ(''); boxRef.current?.focus(); };
  const remove = (email: string) => onChange(value.filter(e => e !== email));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        {value.length === 0 && (
          <span className="text-xs text-grayscale-1100">Pick at least one admin. Super admins always see it.</span>
        )}
        {value.map(e => {
          const a = byEmail[e];
          return (
            <span key={e} title={e}
              className={`inline-flex items-center gap-1.5 rounded-full border pl-2.5 pr-1 py-0.5 text-xs whitespace-nowrap
                ${a && !a.active ? 'bg-grayscale-100 border-grayscale-300 text-grayscale-1100'
                  : 'bg-primary-50 border-primary-300 text-grayscale-1300'}`}>
              <span className="font-medium max-w-[16rem] truncate">{a?.name || e}</span>
              {a?.name && <span className="text-grayscale-1100 max-w-[14rem] truncate">{e}</span>}
              {a && !a.active && <span className="text-grayscale-1100">· inactive</span>}
              {!a && listed && <span className="text-grayscale-1100">· no longer an admin</span>}
              <button type="button" disabled={disabled} onClick={() => remove(e)} aria-label={`Remove ${e}`}
                className="w-4 h-4 rounded-full hover:bg-primary-200 text-grayscale-1100 leading-none disabled:opacity-50">×</button>
            </span>
          );
        })}
      </div>
      <div className="relative max-w-md">
        <input ref={boxRef} value={q} disabled={disabled}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={e => {
            if (e.key === 'Enter' && matches[0]) { e.preventDefault(); add(matches[0].email); }
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder="Add an admin: type a name or e-mail"
          aria-label="Add an admin"
          className="h-8 w-full px-2.5 rounded-md border-[0.5px] border-primary-300 bg-white text-xs disabled:opacity-60" />
        {open && !disabled && (
          <ul role="listbox"
            className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-grayscale-300 bg-white shadow-lg py-1">
            {matches.length === 0 && (
              <li className="px-3 py-2 text-xs text-grayscale-1100">
                {listState === 'loading' ? 'Loading the admin list…'
                  : listState === 'error' || !admins.length ? 'The admin list could not be loaded.' : 'No other admin matches.'}
              </li>
            )}
            {matches.map(a => (
              <li key={a.email} role="option" aria-selected={false}
                onMouseDown={e => e.preventDefault()} onClick={() => add(a.email)}
                className="px-3 py-1.5 text-xs cursor-pointer hover:bg-primary-50 flex items-center gap-2">
                <span className="font-medium text-grayscale-1300 truncate">{a.name || a.email}</span>
                {a.name && <span className="text-grayscale-1100 truncate">{a.email}</span>}
                {!a.active && <span className="ml-auto text-grayscale-1100 shrink-0">inactive</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/** A plain pill, e.g. "New" next to a default-hidden group. */
export const Tag: React.FC<{ tone?: 'new' | 'grey' | 'green' | 'amber' | 'violet'; children: React.ReactNode; title?: string }> =
  ({ tone = 'grey', children, title }) => {
  const look = {
    new: 'bg-sky-50 text-sky-800 border-sky-200',
    grey: 'bg-grayscale-100 text-grayscale-1200 border-grayscale-300',
    green: 'bg-green-50 text-green-800 border-green-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    violet: 'bg-violet-50 text-violet-800 border-violet-200',
  }[tone];
  return (
    <span title={title}
      className={`inline-flex items-center rounded-full border px-2 py-[2px] text-[10px] font-semibold leading-none whitespace-nowrap ${look}`}>
      {children}
    </span>
  );
};
